import { z } from 'zod'

/**
 * PATCH-schema afleiden van een create-schema, zonder de defaults.
 *
 * Zod 4 past een `.default()` óók toe binnen `.partial()`/`.optional()`. Een
 * update-schema als `createXSchema.partial()` vult dus elk weggelaten veld met
 * zijn create-default in, en de handler schrijft dat terug: een gedeeltelijke
 * PATCH zette zo een leverancier terug naar KLANT, een BV terug op 30 dagen
 * betaaltermijn en een concept-rolprofiel weer op concept.
 *
 * `partialZonderDefaults(createXSchema)` haalt de top-level defaults eraf en
 * maakt daarna alles optioneel: een ontbrekend veld blijft `undefined`, dus
 * onaangeraakt. Object-config (strict/passthrough) blijft behouden. Ketting
 * verder zoals bij `.partial()` (`.extend()`, `.refine()`); een `.omit()` doe
 * je vóóraf op het argument.
 *
 * Alleen een top-level `.default()` als buitenste laag wordt verwijderd
 * (`z.boolean().default(true)`, `z.boolean().optional().default(true)`). Een
 * default in een genest object blijft staan: stuur je dat object mee, dan
 * hoort het compleet te zijn.
 *
 * De drift-guard `apps/api/src/lib/__tests__/partial-default-drift.test.ts`
 * faalt op een nieuwe `.partial()` op een schema met een `.default()`.
 */

type ZonderDefault<T> = T extends z.ZodDefault<infer Binnen> ? Binnen : T

type ShapeZonderDefaults<Shape extends z.ZodRawShape> = {
  [K in keyof Shape]: ZonderDefault<Shape[K]>
}

export function zonderDefaults<Shape extends z.ZodRawShape, Config extends z.core.$ZodObjectConfig>(
  schema: z.ZodObject<Shape, Config>,
) {
  const shape: Record<string, z.core.$ZodType> = {}
  for (const [sleutel, veld] of Object.entries(schema.shape)) {
    shape[sleutel] = veld instanceof z.ZodDefault ? veld.unwrap() : veld
  }
  return schema.extend(shape as ShapeZonderDefaults<Shape>)
}

export function partialZonderDefaults<Shape extends z.ZodRawShape, Config extends z.core.$ZodObjectConfig>(
  schema: z.ZodObject<Shape, Config>,
) {
  return zonderDefaults(schema).partial()
}
